import { useState, type ReactNode } from 'react'
import { palette, typography } from '@versports/theme'
import { useImageStatus } from './useImageStatus'

/**
 * Shared bottom navigation. Rendered both in `web-app` (under react-router)
 * and in `vr-app` (under its own LandingOverlay). To keep the component
 * router-agnostic, navigation is delegated via two props:
 *
 *   - `currentPath`  — caller tells us which item is active. In the web-app
 *                      this is `useLocation().pathname`; in the vr-app it's
 *                      `window.location.pathname` (e.g. `/vr/`).
 *   - `onNavigate`   — caller decides what "go" means. The web-app pushes
 *                      via react-router; the vr-app does a full-page
 *                      `window.location.href = href`.
 *
 * The `VR` item is special: it's an *action*, not a navigation, so it
 * has its own `onTrain` async handler. The caller is responsible for the
 * Bearer-token bridge (`POST /auth/exchange-token`) and the redirect to
 * `/vr/?token=...`; this component just shows the right loading/error UI.
 * Reemplaza al par "Inicio" + "Train" que existió hasta 2026-09-07 (decisión
 * de producto mg, doc 32 §4): el jugador no tiene una home separada del
 * juego — la superficie de juego ES el lobby del vr-app, y los gates
 * (consentimiento → cuestionarios → sensores) corren adentro del vr-app
 * después de "Comenzar". El tab se llamó "Jugar", después
 * **"Inicio"** (2026-09-09) y desde el 2026-09-18 **"VR"**: la etiqueta
 * nombra a dónde lleva, que es `/vr`, y no una home que el jugador no tiene
 * (mg: *"'inicio' debería ser VR en bottom bar si va a mandar a /vr"*).
 * Mismo ícono de visor y misma acción `onTrain` en las tres vueltas; el prop
 * sigue llamándose `onTrain` para no romper el call site del vr-app
 * (`vr-app/src/App.tsx`).
 *
 * Items: VR / Data / Guía + los opcionales Club, Tablet y Admin, que el caller
 * gatea por rol. Con cinco o seis presentes la fila entra en modo `compact`
 * (tracking, padding y ancho mínimo más chicos) para no desbordar en un
 * viewport de 320 px.
 *
 * Visibility: bottom-fixed across all viewports, with `safe-area-inset-bottom`
 * padding for notched phones. Both apps must leave ~76px of bottom padding on
 * their main content so the bar doesn't cover the last lines.
 */

export interface NavBarProps {
  currentPath: string
  onNavigate: (href: string) => void
  /** Async — triggers exchange-token + redirect to the vr-app. Errors get
   *  surfaced as a toast above the bar. */
  onTrain: () => void | Promise<void>
  /** If true, renders the Admin item. The caller decides (typically based
   *  on `user.is_superuser` or `user.roles.includes('superadmin')`). */
  showAdmin?: boolean
  /** If true, renders a Club item between Data and Admin (if present). */
  showClub?: boolean
  /** If true, renders a Tablet item (`/tablet`, el modo kiosco de la tablet
   *  compartida del kit) entre Club y Admin. Superficie de STAFF: la ruta es
   *  pública (su credencial es el código del kit), así que este item es lo
   *  único que decide quién la ve — el caller lo gatea con el mismo criterio
   *  que `showClub`/`showAdmin` (ver `web-app/src/components/AppNavBar.tsx`). */
  showTablet?: boolean
  /** Accent color to override palette.primary for borders, backgrounds, etc.
   *  Omit or pass undefined to use the default palette.primary. */
  accent?: string
  /** Club logo URL. If provided, renders in the Club item instead of generic icon. */
  clubLogoUrl?: string
  /** Hash corto del build (el vr-app pasa el sha7 del commit desplegado). Se
   *  muestra chico, a la izquierda de la barra; por debajo de 640 px se oculta
   *  para no pisar los items (sigue en el modal "Acerca de"). */
  buildHash?: string
}

export function NavBar({ currentPath, onNavigate, onTrain, showAdmin, showClub, showTablet, accent, clubLogoUrl, buildHash }: NavBarProps) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isHome = currentPath === '/'
  const isData = currentPath.startsWith('/data')
  const isClub = currentPath.startsWith('/club')
  const isTablet = currentPath.startsWith('/tablet')
  const isAdminPath = currentPath.startsWith('/admin')
  const isVR = currentPath.startsWith('/vr')
  const accentColor = accent ?? palette.primary

  // Hasta seis destinos deben entrar en 320 px sin imponer anchos mínimos.
  const itemCount = 3 + (showClub ? 1 : 0) + (showTablet ? 1 : 0) + (showAdmin ? 1 : 0)
  const compact = itemCount > 4

  async function handleTrain() {
    setBusy(true)
    setError(null)
    try {
      await onTrain()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo abrir el VR.')
      setBusy(false)
    }
  }

  return (
    <>
      {error && (
        <div
          className="fixed bottom-24 left-1/2 z-50 -translate-x-1/2 rounded-sm border border-danger/30 bg-danger/10 px-md py-sm text-sm text-danger"
          role="alert"
        >
          {error}
        </div>
      )}
      <nav
        aria-label="Navegación principal"
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 40,
          borderTop: `2px solid ${accentColor}`,
          background: `linear-gradient(180deg, ${palette.surfaceElevated}f2 0%, ${palette.surface}f2 100%)`,
          boxShadow: `0 -18px 46px ${accentColor}38`,
          backdropFilter: 'blur(14px)',
          paddingBottom: 'env(safe-area-inset-bottom)',
          fontFamily: typography.family,
        }}
      >
        {buildHash && (
          <>
            <style>{'@media (max-width: 639px) { .vrs-navbar-build { display: none; } }'}</style>
            <span
              className="vrs-navbar-build"
              title={`build ${buildHash}`}
              style={{
                position: 'absolute',
                left: 'calc(16px + env(safe-area-inset-left))',
                top: '50%',
                transform: 'translateY(-50%)',
                fontFamily: typography.monoFamily,
                fontSize: 11,
                letterSpacing: '0.04em',
                color: palette.textMuted,
                pointerEvents: 'none',
              }}
            >
              {buildHash}
            </span>
          </>
        )}
        <div
          style={{
            maxWidth: 448,
            margin: '0 auto',
            display: 'flex',
            alignItems: 'stretch',
            justifyContent: 'space-between',
            gap: compact ? 4 : 6,
            padding: compact ? '8px 6px' : '8px 8px',
          }}
        >
          <NavItem
            active={isVR || isHome}
            onClick={handleTrain}
            disabled={busy}
            icon={<IconVR />}
            label="VR"
            accentColor={accentColor}
            compact={compact}
          />
          <NavItem
            active={isData}
            onClick={() => onNavigate('/data')}
            icon={<IconBook />}
            label="Data"
            accentColor={accentColor}
            compact={compact}
          />
          {showClub && (
            <NavItem
              active={isClub}
              onClick={() => onNavigate('/club')}
              icon={<ClubIcon logoUrl={clubLogoUrl} />}
              label="Club"
              accentColor={accentColor}
              compact={compact}
            />
          )}
          {showTablet && (
            <NavItem
              active={isTablet}
              onClick={() => onNavigate('/tablet')}
              icon={<IconTablet />}
              label="Tablet"
              accentColor={accentColor}
              compact={compact}
            />
          )}
          <NavItem
            active={currentPath.startsWith('/guia')}
            onClick={() => onNavigate('/guia')}
            icon={<IconGuide />}
            label="Guía"
            accentColor={accentColor}
            compact={compact}
          />
          {showAdmin && (
            <NavItem
              active={isAdminPath}
              onClick={() => onNavigate('/admin')}
              icon={<IconShield />}
              label="Admin"
              accentColor={accentColor}
              compact={compact}
            />
          )}
        </div>
      </nav>
    </>
  )
}

function NavItem({
  active,
  onClick,
  disabled,
  icon,
  label,
  accentColor,
  compact,
}: {
  active: boolean
  onClick: () => void | Promise<void>
  disabled?: boolean
  icon: ReactNode
  label: string
  accentColor?: string
  compact?: boolean
}) {
  const color = accentColor ?? palette.primary
  return (
    <button
      type="button"
      onClick={() => void onClick()}
      disabled={disabled}
      aria-current={active ? 'page' : undefined}
      style={{
        flex: '1 1 0',
        minWidth: compact ? 0 : 64,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        padding: compact ? '9px 4px' : '9px 8px',
        borderRadius: 10,
        border: `1px solid ${active ? color : 'transparent'}`,
        background: active ? color : 'transparent',
        color: active ? palette.primaryFg : palette.textSecondary,
        boxShadow: active ? `0 0 18px ${color}51` : 'none',
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background 140ms ease, border-color 140ms ease, color 140ms ease',
      }}
    >
      <span style={{ lineHeight: 1 }}>{icon}</span>
      <span
        style={{
          fontFamily: typography.monoFamily,
          fontSize: compact ? 9 : 10,
          fontWeight: 500,
          letterSpacing: compact ? '0.1em' : '0.18em',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </span>
    </button>
  )
}

// Inline SVG icons — no external deps, color via `currentColor`.

function IconVR() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="2" y="7" width="20" height="10" rx="3" />
      <circle cx="8" cy="12" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="16" cy="12" r="1.6" fill="currentColor" stroke="none" />
      <path d="M12 17v2" />
    </svg>
  )
}
function IconBook() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 5a2 2 0 0 1 2-2h12v16H6a2 2 0 0 0-2 2V5z" />
      <path d="M4 5v15" />
      <path d="M8 7h6M8 11h6" />
    </svg>
  )
}
function IconShield() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 3l8 3v6c0 4.5-3.5 8.5-8 9-4.5-.5-8-4.5-8-9V6l8-3z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  )
}
function IconTablet() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="4" y="2" width="16" height="20" rx="2.5" />
      <path d="M10 18.5h4" />
    </svg>
  )
}
/**
 * Ícono del item Club: el logo del club si carga, el `IconClub` genérico si
 * no hay `logoUrl`, mientras carga, o si el asset está caído.
 *
 * Invariante (bug mg 2026-09-07): nunca se ve el ícono de imagen rota del
 * browser ni el texto del `alt`. El logo se precarga fuera del árbol con
 * `useImageStatus` (reintentos con backoff, cancelados al desmontar) y el
 * `<img>` visible se monta recién cuando cargó — montar un `<img>` y esperar
 * el `onError` llegaba tarde, porque la request abortada por un cambio de ruta
 * o un remonte del árbol alcanzaba a pintar el glifo. `alt=""` como red de
 * contención adicional.
 */
function ClubIcon({ logoUrl }: { logoUrl?: string }) {
  const src = logoUrl?.trim() || undefined
  const status = useImageStatus(src)
  // Anclado al `src` que falló: cambiar de club arranca limpio.
  const [visibleFailed, setVisibleFailed] = useState<string | null>(null)

  if (!src || status !== 'loaded' || visibleFailed === src) return <IconClub />

  return (
    <img
      src={src}
      alt=""
      decoding="async"
      style={{ width: 22, height: 22, objectFit: 'contain' }}
      onError={() => setVisibleFailed(src)}
    />
  )
}

function IconClub() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="9" r="3" />
      <path d="M7 15h10a4 4 0 0 1 4 4v2H3v-2a4 4 0 0 1 4-4z" />
    </svg>
  )
}

function IconGuide() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path d="m16 8-2.5 5.5L8 16l2.5-5.5L16 8Z" />
    </svg>
  )
}
